import type { Canvas } from 'fabric';
import { loadFabric } from './fabricLoader';
import { getASTComponentById } from '../store/common/action';
import { astStore } from '../store';
import {
  CANVAS_DOWNLOAD_WITHOUT_WATERMARK,
  CANVAS_EXPORT_WATERMARK,
} from '../constant/common';
import { triggerFileDownload } from './imageDownloadUtil';

/**
 * Fabric.js 画布工具。
 * 初始化后 DOM 结构为：container → canvas-container → lower-canvas + upper-canvas（Fabric 自动创建）。
 */

export type FabricCanvasInstance = Canvas;

export interface FabricCanvasOptions {
  readOnly?: boolean;
  backgroundColor?: string;
  onChange?: (json: string) => void;
}

/** 海报等高清场景默认导出倍率（相对画布显示尺寸） */
export const POSTER_EXPORT_MULTIPLIER = 2;

export { CANVAS_EXPORT_WATERMARK } from '../constant/common';

/** 水印字体与颜色（粗体圆角无衬线、浅灰蓝、无背景） */
const CANVAS_WATERMARK_FONT_FAMILY = '"PingFang SC", "HarmonyOS Sans SC", "Microsoft YaHei UI", "Segoe UI", sans-serif';
const CANVAS_WATERMARK_COLOR = 'rgba(168, 178, 192, 0.88)';

export interface FabricImageExportOptions {
  format?: 'png' | 'jpeg';
  multiplier?: number;
  quality?: number;
  filename?: string;
  watermarkText?: string;
}

/** 将 canvas_ast 原始值（字符串或对象）解析为 Fabric loadFromJSON 可用的对象 */
const parseCanvasAst = (value: unknown): Record<string, unknown> | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

/** 从组件 attrs 中提取 canvas_ast 序列化字符串 */
export const getCanvasAstValue = (attrs: object): string => {
  const raw = _get(attrs, ['canvas_ast', 'value'], '');
  if (raw === null || raw === undefined) {
    return '';
  }
  if (typeof raw === 'string') {
    return raw;
  }
  try {
    return JSON.stringify(raw);
  } catch {
    return '';
  }
};

/** 根据全局常量解析导出时应使用的水印文案，空字符串表示不加水印 */
export const getCanvasExportWatermarkText = (): string => {
  return CANVAS_DOWNLOAD_WITHOUT_WATERMARK ? '' : CANVAS_EXPORT_WATERMARK;
};

/** 构建画布图片导出参数（水印由全局常量 CANVAS_DOWNLOAD_WITHOUT_WATERMARK 控制） */
export const buildCanvasImageExportOptions = (
  component: ComponentItem,
  overrides: FabricImageExportOptions = {},
): FabricImageExportOptions => ({
  filename: getCanvasExportFilename(component),
  format: 'png',
  watermarkText: getCanvasExportWatermarkText(),
  ...overrides,
});

/** 读取容器实际像素尺寸，最小为 1 避免 Fabric 初始化异常 */
export const getContainerSize = (container: HTMLElement) => {
  const width = Math.max(container.clientWidth, 1);
  const height = Math.max(container.clientHeight, 1);
  return { width, height };
};

/**
 * 创建并初始化 Fabric Canvas 实例。
 * @param canvasEl 组件 render 中的原生 canvas 元素
 * @param container 尺寸观测容器（custom-atom-canvas__container）
 * @param astValue canvas_ast JSON 字符串，为空则显示空白画布
 * @param options.readOnly 运行态展示时为 true，禁用交互
 * @param options.onChange 设计态编辑回调，object 增删改时触发
 */
export const createFabricCanvas = async (
  canvasEl: HTMLCanvasElement,
  container: HTMLElement,
  astValue: string,
  options: FabricCanvasOptions = {},
): Promise<FabricCanvasInstance> => {
  const fabric = await loadFabric();
  const { width, height } = getContainerSize(container);

  const canvas = new fabric.Canvas(canvasEl, {
    width,
    height,
    backgroundColor: options.backgroundColor ?? '#ffffff',
    selection: !options.readOnly,
  });

  // 运行态只读展示：禁用选中与事件；设计态编辑时 readOnly 为 false，不在此分支
  if (options.readOnly) {
    canvas.selection = false;
    canvas.skipTargetFind = true;
    canvas.forEachObject((obj) => {
      obj.selectable = false;
      obj.evented = false;
    });
  }

  const ast = parseCanvasAst(astValue);
  if (ast) {
    await canvas.loadFromJSON(ast);
    canvas.renderAll();
    if (options.readOnly) {
      canvas.forEachObject((obj) => {
        obj.selectable = false;
        obj.evented = false;
      });
    }
  }

  if (options.onChange && !options.readOnly) {
    const persist = () => {
      options.onChange?.(JSON.stringify(canvas.toJSON()));
    };
    canvas.on('object:modified', persist);
    canvas.on('object:added', persist);
    canvas.on('object:removed', persist);
  }

  return canvas;
};

/** 容器尺寸变化时同步 Fabric 画布宽高并重绘 */
export const resizeFabricCanvas = (canvas: FabricCanvasInstance, container: HTMLElement) => {
  const { width, height } = getContainerSize(container);
  canvas.setDimensions({ width, height });
  canvas.renderAll();
};

/** 销毁 Fabric 实例并移除其自动创建的 canvas-container 等 DOM 节点 */
export const disposeFabricCanvas = async (canvas: FabricCanvasInstance | null) => {
  if (!canvas) {
    return;
  }
  await canvas.dispose();
};

/** 在导出图片右下角绘制水印 */
const applyWatermarkToCanvas = (
  sourceCanvas: HTMLCanvasElement,
  watermarkText: string,
): HTMLCanvasElement => {
  const output = document.createElement('canvas');
  output.width = sourceCanvas.width;
  output.height = sourceCanvas.height;

  const ctx = output.getContext('2d');
  if (!ctx) {
    return sourceCanvas;
  }

  ctx.drawImage(sourceCanvas, 0, 0);

  const fontSize = Math.max(Math.round(output.width * 0.028), 16);
  const padding = Math.max(Math.round(output.width * 0.018), 12);

  ctx.font = `bold ${fontSize}px ${CANVAS_WATERMARK_FONT_FAMILY}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = CANVAS_WATERMARK_COLOR;
  ctx.fillText(watermarkText, output.width - padding, output.height - padding);

  return output;
};

const canvasElementToBlob = (
  canvasEl: HTMLCanvasElement,
  format: 'png' | 'jpeg',
  quality: number,
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvasEl.toBlob(
      (blob) => resolve(blob),
      format === 'jpeg' ? 'image/jpeg' : 'image/png',
      quality,
    );
  });
};

/**
 * 将 Fabric 画布导出为高清 PNG/JPEG 并触发浏览器下载。
 * 导出前会取消选中态并重绘，multiplier 越大分辨率越高（适合海报）。
 * 导出图片右下角会自动添加水印。
 */
export const downloadFabricCanvasAsImage = async (
  canvas: FabricCanvasInstance,
  options: FabricImageExportOptions = {},
): Promise<boolean> => {
  const {
    filename = `canvas-${Date.now()}.png`,
    format = 'png',
    multiplier = POSTER_EXPORT_MULTIPLIER,
    quality = 1,
    watermarkText = getCanvasExportWatermarkText(),
  } = options;

  canvas.discardActiveObject();
  canvas.renderAll();

  try {
    const sourceCanvas = canvas.toCanvasElement(multiplier);
    const exportCanvas = watermarkText
      ? applyWatermarkToCanvas(sourceCanvas, watermarkText)
      : sourceCanvas;
    const blob = await canvasElementToBlob(exportCanvas, format, quality);
    if (!blob) {
      return false;
    }

    const ext = format === 'jpeg' ? 'jpg' : 'png';
    const downloadName = /\.(png|jpe?g)$/i.test(filename) ? filename : `${filename}.${ext}`;
    const url = URL.createObjectURL(blob);
    triggerFileDownload(url, downloadName);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('导出 canvas 图片失败:', error);
    return false;
  }
};

/** 从组件 attrs 生成导出文件名 */
export const getCanvasExportFilename = (component: ComponentItem): string => {
  const title = _get(component, ['attrs', 'title', 'value'], '');
  const baseName = typeof title === 'string' && title.trim() ? title.trim() : 'canvas-poster';
  return baseName.replace(/[\\/:*?"<>|]/g, '_');
};

/**
 * 导出设计画板上指定 canvas 组件为高清 PNG。
 * 优先调用 design-custom-atom-canvas.exportPosterImage()，否则从 AST 离屏渲染导出。
 */
export const exportDesignBoardCanvasById = async (componentId: string): Promise<boolean> => {
  const component = getASTComponentById(componentId);
  if (!component || component.type !== 'canvas') {
    return false;
  }

  const designEl = document.querySelector(
    `design-custom-atom-canvas[data-design-comp-id="${componentId}"]`,
  ) as HTMLDesignCustomAtomCanvasElement | null;

  if (designEl?.exportPosterImage) {
    return designEl.exportPosterImage();
  }

  return exportCanvasFromAst(component, componentId);
};

/** 从 canvas_ast 离屏渲染并导出（设计态 DOM 未挂载时的兜底） */
export const exportCanvasFromAst = async (
  component: ComponentItem,
  componentId: string,
  exportOptions: FabricImageExportOptions = {},
): Promise<boolean> => {
  const compEl = document.getElementById(componentId);
  const width = Math.max(compEl?.clientWidth ?? 800, 1);
  const height = Math.max(compEl?.clientHeight ?? 600, 1);

  const container = document.createElement('div');
  container.style.cssText = `position:fixed;left:-9999px;width:${width}px;height:${height}px;`;
  const canvasEl = document.createElement('canvas');
  container.appendChild(canvasEl);
  document.body.appendChild(container);

  let fabricCanvas: FabricCanvasInstance | null = null;
  try {
    fabricCanvas = await createFabricCanvas(
      canvasEl,
      container,
      getCanvasAstValue(_get(component, 'attrs', {})),
      { readOnly: true },
    );
    return await downloadFabricCanvasAsImage(fabricCanvas, buildCanvasImageExportOptions(
      component,
      exportOptions,
    ));
  } catch (error) {
    console.error('离屏导出 canvas 失败:', error);
    return false;
  } finally {
    await disposeFabricCanvas(fabricCanvas);
    container.remove();
  }
};

/**
 * 获取设计 AST 中所有 canvas 组件 id。
 */
export const getDesignBoardCanvasComponentIds = (): string[] => {
  return astStore.state.components
    .filter((comp) => comp.type === 'canvas')
    .map((comp) => comp.prop);
};

/**
 * 确定本次导出的 canvas id：优先导出选中项里的 canvas，否则导出页面上全部 canvas。
 */
export const resolveCanvasExportTargetIds = (selectedIds: Iterable<string>): string[] => {
  const selectedCanvasIds = [...selectedIds].filter(
    (id) => getASTComponentById(id)?.type === 'canvas',
  );
  if (selectedCanvasIds.length > 0) {
    return selectedCanvasIds;
  }
  return getDesignBoardCanvasComponentIds();
};

/**
 * 导出设计画板 canvas 组件为高清 PNG。
 * 有选中 canvas 时只导出选中项；未选中时导出页面上全部 canvas。
 */
export const exportDesignBoardCanvases = async (
  selectedIds: Iterable<string> = [],
): Promise<{ success: number; failed: number; total: number }> => {
  const ids = resolveCanvasExportTargetIds(selectedIds);
  let success = 0;
  let failed = 0;

  for (const id of ids) {
    const ok = await exportDesignBoardCanvasById(id);
    if (ok) {
      success++;
    } else {
      failed++;
    }
  }

  return { success, failed, total: ids.length };
};
